import { Directive, ElementRef, HostListener, Input, OnDestroy, Optional, Renderer2, input } from '@angular/core';
import { NgModel } from '@angular/forms';
import { cloneDeep } from 'lodash';

@Directive({
  selector: '[tooltipCustom]',
  standalone: true,
})
export class TooltipCustomDirective implements OnDestroy {
  /**
   * @description use this.text instead of this.value when this.text is truthy
   */
  readonly text = input<string | any>('', { alias: 'appTooltip' });

  /**
   * @description the place that tooltip should be shown with target element
   */
  readonly placement = input<'bottom' | 'top'>('bottom');

  /**
   * @description if dxc-input-text, disabled should be true, another elements don't need this property
   */
  readonly disabled = input<boolean>(false);

  /**
   * @description value from [dxc] - input text, date, select
   */
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() value: string | any = '';

  /**
   * @description value from dxc-select
   */
  
  readonly options = input<any[]>([]);

  /**
   * @description  show tooltip when enable
   */
  readonly ignoreDisable = input<boolean>(false);
  readonly offset = input<number>(4);
  readonly anchorSelector = input<string>('');

  ngModelValue: string = '';
  el: any;
  container: any;
  inner: any;
  arrow: any;
  parent: any;
  tagName: string = 'CUSTOM-INPUT-TEXT';
  isClickClosed: boolean = false;
  private touchTimer: any = null;
  private autoDismissTimer: any = null;
  private lastTouchTime: number = 0;
  private readonly LONG_PRESS_MS = 500;
  private readonly MOBILE_AUTO_DISMISS_MS = 3000;
  private readonly TOUCH_TO_MOUSE_GUARD_MS = 800;

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
    @Optional() private ngModel: NgModel
  ) {
    this.el = this.elRef.nativeElement as HTMLElement;
    this.tagName = this.el.tagName || 'CUSTOM-INPUT-TEXT';
    this.tagName !== 'CUSTOM-INPUT-TEXT' && (this.value = this.el.innerText);
  }

  @HostListener('window:resize')
  sizeChange(): void {
    this.caculatePosition();
  }

  @HostListener('mouseenter')
  mouseEnter(): void {
    if (this.isRecentTouch()) return;
    if (this.isClickClosed) return;
    if (!this.canInitTooltip()) return;
    if (!this.hasValue()) return;
    if (this.tagName == 'LABEL' && this.el.offsetWidth >= this.el.scrollWidth) return;
    this.initTooltip();
    this.setText();
    this.tagName == 'LABEL' && this.renderer.setStyle(this.parent, 'z-index', '2');
  }

  @HostListener('valueChange', ['$event'])
  onValueChange(event: any): void {
    if (this.isTooltipInitialized()) {
      this.removeTooltip();
    }
    this.isClickClosed = false;
  }

  @HostListener('mouseleave')
  mouseLeave(): void {
    if (this.isRecentTouch()) return;
    if (!this.isTooltipInitialized()) return;
    this.removeTooltip();
    this.tagName == 'LABEL' && this.renderer.setStyle(this.parent, 'z-index', '1');
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    if (this.isRecentTouch()) return;
    if (this.isTooltipInitialized()) {
      this.removeTooltip();
      this.isClickClosed = true;
    }
  }

  private isRecentTouch(): boolean {
    return this.lastTouchTime > 0 && Date.now() - this.lastTouchTime < this.TOUCH_TO_MOUSE_GUARD_MS;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    this.isClickClosed = false;
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (this.isClickClosed) return;
    if (!this.canInitTooltip()) return;
    if (!this.hasValue()) return;
    if (this.tagName == 'LABEL' && this.el.offsetWidth >= this.el.scrollWidth) return;
    this.clearTouchTimer();
    this.touchTimer = setTimeout(() => {
      this.initTooltip();
      this.setText();
      this.scheduleAutoDismiss();
    }, this.LONG_PRESS_MS);
  }

  @HostListener('touchend')
  onTouchEnd(): void {
    this.clearTouchTimer();
    this.lastTouchTime = Date.now();
  }

  @HostListener('touchmove')
  onTouchMove(): void {
    this.clearTouchTimer();
  }

  @HostListener('touchcancel')
  onTouchCancel(): void {
    this.clearTouchTimer();
  }

  @HostListener('document:touchstart', ['$event'])
  onDocumentTouchStart(event: TouchEvent): void {
    if (!this.isTooltipInitialized()) return;
    const target = event.target as Node;
    if (this.el.contains(target)) return;
    this.removeTooltip();
    this.clearAutoDismiss();
  }

  /* WRITTEN BY Claude Code */
  // position:fixed tooltip detaches from its field during scroll
  @HostListener('window:scroll')
  @HostListener('document:scroll')
  onWindowScroll(): void {
    if (!this.isTooltipInitialized()) return;
    this.removeTooltip();
    this.clearAutoDismiss();
  }
  /* END Claude Code */

  private clearTouchTimer(): void {
    if (this.touchTimer) {
      clearTimeout(this.touchTimer);
      this.touchTimer = null;
    }
  }

  private clearAutoDismiss(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
  }

  private scheduleAutoDismiss(): void {
    this.clearAutoDismiss();
    this.autoDismissTimer = setTimeout(() => {
      if (this.isTooltipInitialized()) {
        this.removeTooltip();
      }
      this.autoDismissTimer = null;
    }, this.MOBILE_AUTO_DISMISS_MS);
  }

  ngOnDestroy(): void {
    this.clearTouchTimer();
    this.clearAutoDismiss();
    if (this.isTooltipInitialized()) {
      this.removeTooltip();
    }
  }

  ngAfterViewInit(): void {
    if (!this.canInitTooltip()) return;

    this.parent = this.el.parentNode;
    if (this.tagName == 'LABEL') {
      this.value = cloneDeep(this.el.innerText);
    }
  }
  getAnchorRect(): DOMRect {
    if (this.anchorSelector()) {
      const target = this.el.querySelector(this.anchorSelector());
      if (target) return (target as HTMLElement).getBoundingClientRect();
    }
    const full = this.el.getBoundingClientRect();
    const errorEl = this.el.querySelector('span.errorMessage');
    if (!errorEl) return full;

    const h = (errorEl as HTMLElement).offsetHeight;
    return {
      x: full.x,
      y: full.y,
      left: full.left,
      top: full.top,
      width: full.width,
      height: full.height - h,
      right: full.right,
      bottom: full.bottom - h,
      toJSON: () => {},
    } as DOMRect;
  }
  initTooltip(): void {
    if (!this.canInitTooltip()) return;
    /* WRITTEN BY Claude Code */
    // double-init orphans the previous container on document.body
    if (this.isTooltipInitialized()) {
      this.removeTooltip();
    }
    /* END Claude Code */

    this.container = this.renderer.createElement('div');
    this.renderer.setStyle(this.container, 'position', 'fixed');
    this.renderer.setStyle(this.container, 'background', '#fff');
    this.renderer.setStyle(this.container, 'color', '#000');
    this.renderer.setStyle(this.container, 'padding', '4px 10px');
    this.renderer.setStyle(this.container, 'border', '1px solid #000');
    this.renderer.setStyle(this.container, 'border-radius', '4px');
    this.renderer.setStyle(this.container, 'white-space', 'nowrap');
    this.renderer.setStyle(this.container, 'font-size', '12px');
    this.renderer.setStyle(this.container, 'z-index', '999999');
    this.renderer.setStyle(this.container, 'box-sizing', 'border-box');

    this.inner = this.renderer.createElement('div');
    this.renderer.appendChild(this.container, this.inner);

    this.arrow = this.renderer.createElement('div');
    this.renderer.setStyle(this.arrow, 'position', 'absolute');
    this.renderer.setStyle(this.arrow, 'left', '50%');
    this.renderer.setStyle(this.arrow, 'transform', 'translateX(-50%)');
    this.renderer.setStyle(this.arrow, 'width', '0');
    this.renderer.setStyle(this.arrow, 'height', '0');

    const place = this.placement();
    const gap = this.offset();
    if (place === 'bottom') {
      this.renderer.setStyle(this.arrow, 'top', `-${gap}px`);
      this.renderer.setStyle(this.arrow, 'border-left', `${gap}px solid transparent`);
      this.renderer.setStyle(this.arrow, 'border-right', `${gap}px solid transparent`);
      this.renderer.setStyle(this.arrow, 'border-bottom', `${gap}px solid #000`);
    } else {
      this.renderer.setStyle(this.arrow, 'bottom', `-${gap}px`);
      this.renderer.setStyle(this.arrow, 'border-left', `${gap}px solid transparent`);
      this.renderer.setStyle(this.arrow, 'border-right', `${gap}px solid transparent`);
      this.renderer.setStyle(this.arrow, 'border-top', `${gap}px solid #000`);
    }

    document.body.appendChild(this.container);
    this.renderer.appendChild(this.container, this.arrow);

    setTimeout(() => {
      this.setText();
      this.caculatePosition();
    });
  }

  removeTooltip(): void {
    if (this.container && document.body.contains(this.container)) {
      document.body.removeChild(this.container);
    }
    this.container = null;
    this.inner = null;
    this.arrow = null;
  }

  caculatePosition(): void {
    if (!this.isTooltipInitialized()) return;
    const rect = this.getAnchorRect();
    const tooltipRect = this.container.getBoundingClientRect();
    let tx = rect.left + (rect.width - tooltipRect.width) / 2;
    tx = Math.max(4, Math.min(tx, window.innerWidth - tooltipRect.width - 4));

    const place = this.placement();
    const gap = this.offset();
    let ty: number;

    if (place === 'bottom') {
      ty = rect.bottom + gap;
      if (ty + tooltipRect.height > window.innerHeight) {
        ty = rect.top - tooltipRect.height - gap;
        this.renderer.setStyle(this.arrow, 'top', 'auto');
        this.renderer.setStyle(this.arrow, 'bottom', `-${gap}px`);
        this.renderer.setStyle(this.arrow, 'border-bottom', 'none');
        this.renderer.setStyle(this.arrow, 'border-top', `${gap}px solid #000`);
      }
    } else {
      ty = rect.top - tooltipRect.height - gap;
      if (ty < 0) {
        ty = rect.bottom + gap;
        this.renderer.setStyle(this.arrow, 'bottom', 'auto');
        this.renderer.setStyle(this.arrow, 'top', `-${gap}px`);
        this.renderer.setStyle(this.arrow, 'border-top', 'none');
        this.renderer.setStyle(this.arrow, 'border-bottom', `${gap}px solid #000`);
      }
    }

    this.renderer.setStyle(this.container, 'left', `${tx}px`);
    this.renderer.setStyle(this.container, 'top', `${ty}px`);
  }
  // setText(): void {
  //   if (!this.isTooltipInitialized()) return;
  //   this.getNgModelValue();
  //   let text = this.text() || this.value || this.ngModelValue;
  //   const options = this.options();
  //   if (options.length) {
  //     const option = options.find((item: ISelect) => item.value == text);
  //     if (option) {
  //       text = option.label;
  //     } else {
  //       const nonNullOptions = text.filter((option: any) => option !== null);
  //       text = nonNullOptions.map((option: any) => option).join(', ');
  //     }
  //   }
  //   if (text != '') {
  //     this.renderer.setProperty(this.inner, 'innerText', text);
  //     this.caculatePosition();
  //   }
  // }
  setText(): void {
    if (!this.isTooltipInitialized()) return;
    this.getNgModelValue();
    let text = this.text() || this.ngModelValue || this.value;

    // Cast to any to handle the mix of Groups and Options
    const rawOptions = this.options() as any[];

    if (rawOptions && rawOptions.length) {
      const flatOptions: any[] = [];

      // Flatten the array: if it's a group, grab its children; otherwise, keep the item
      rawOptions.forEach((opt) => {
        if (opt && 'options' in opt && Array.isArray(opt.options)) {
          flatOptions.push(...opt.options);
        } else {
          flatOptions.push(opt);
        }
      });

      const foundOption = flatOptions.find((item: any) => item.value == text);

      if (foundOption) {
        text = foundOption.label;
      } else if (Array.isArray(text)) {
        // Logic for multi-select
        text = text
          .filter((val) => val !== null)
          .map((val) => flatOptions.find((o) => o.value === val)?.label || val)
          .join(', ');
      }
    }

    if (text) {
      this.renderer.setProperty(this.inner, 'innerText', text);
      this.caculatePosition();
    }
  }
  /**
   *
   * @description only use tooltip for disabled input
   */
  canInitTooltip(): boolean {
    return true;
  }

  hasValue(): boolean {
    this.getNgModelValue();
    return !!(this.value || this.text() || this.ngModelValue);
  }

  isTooltipInitialized(): boolean {
    return !!(this.container || this.inner || this.arrow);
  }

  getNgModelValue() {
    if (this.ngModel) {
      this.ngModelValue = this.ngModel.model || '';
      return;
    }
    const inputs = this.el.querySelectorAll('input');
    let picked: HTMLInputElement | null = null;
    for (const inp of Array.from(inputs) as HTMLInputElement[]) {
      if (inp.classList.contains('hiddenInput')) continue;
      const style = window.getComputedStyle(inp);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      picked = inp;
      break;
    }
    if (!picked) {
      picked = inputs[0] as HTMLInputElement;
    }
    if (!picked) {
      this.ngModelValue = '';
      return;
    }
    const selectedOption = this.el.querySelector('dxc-select-option-selected .selectedOption') as HTMLElement | null;
    const fallbackText = selectedOption?.textContent?.trim() || '';
    this.ngModelValue = picked.value || fallbackText || picked.placeholder || '';
  }
}
